import React, { useState } from 'react';
import { Menu, User, LogOut, Sun, Moon, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import CurrencyDisplay from './CurrencyDisplay';
import ShortcutBar from './ShortcutBar';
import NotificationMenu from './notifications/NotificationMenu';
import TaskIcon from './tasks/TaskIcon';

/** RBAC rolleri için kısa Türkçe başlık (sağ üst ve menü) */
function getRoleDisplayLabel(role) {
  if (!role) return 'Kullanıcı';
  const labels = {
    admin: 'Sistem Yöneticisi',
    cokYetkili: 'Çok Yetkili',
    yetkili: 'Yetkili',
    tezgahtar: 'Tezgahtar',
    isletme: 'İşletme',
    isletmeHarici: 'İşletme Dışı',
    diger: 'Diğer'
  };
  return labels[role] || role;
}

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const roleLabel = getRoleDisplayLabel(user?.role);

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          <ShortcutBar />
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-3">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {/* Currency Display */}
          <CurrencyDisplay />

          {/* Notifications */}
          <NotificationMenu />

          {/* Tasks */}
          <TaskIcon />

          {/* User menu */}
          <div className="relative group">
            <button className="flex items-center space-x-2 p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.username?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {roleLabel}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.username || 'admin'}
                </p>
              </div>
            </button>

            {/* Dropdown menu */}
            <div className="absolute right-1 sm:right-0 mt-2 w-[calc(100vw-2rem)] sm:w-48 max-w-[calc(100vw-2rem)] sm:max-w-none bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="py-2">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {roleLabel}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.username || 'admin'}
                  </p>
                </div>
                
                <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Profil</span>
                </button>

                <button
                  onClick={() => setPwdOpen(true)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <Lock className="w-4 h-4" />
                  <span>Şifre Değiştir</span>
                </button>
                
                <button
                  onClick={logout}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      {pwdOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-825 rounded-2xl w-full max-w-lg shadow-2xl ring-1 ring-gray-100/60 dark:ring-gray-700/60">
            <div className="px-6 py-5">
              <h3 className="text-xl font-semibold tracking-tight">Şifre Değiştir</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Güvenliğiniz için yeni şifreniz en az 6 karakter olmalıdır.</p>
            </div>
            <div className="px-6 pb-1 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Eski Şifre</label>
                <input type="password" className="input w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 focus:ring-2 focus:ring-blue-500" placeholder="••••••••" value={oldPassword} onChange={e=>setOldPassword(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Yeni Şifre</label>
                <input type="password" className="input w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 focus:ring-2 focus:ring-blue-500" minLength={6} placeholder="en az 6 karakter" value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Yeni Şifre (Tekrar)</label>
                <input type="password" className="input w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 focus:ring-2 focus:ring-blue-500" minLength={6} placeholder="en az 6 karakter" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} />
              </div>
            </div>
            <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
              <button className="btn-secondary" onClick={()=>setPwdOpen(false)}>İptal</button>
              <button className="btn-primary" disabled={saving} onClick={async()=>{
                if (!oldPassword || !newPassword || !confirmPassword) return alert('Tüm alanlar zorunlu');
                if (newPassword.length < 6) return alert('Yeni şifre en az 6 karakter olmalı');
                if (newPassword !== confirmPassword) return alert('Yeni şifreler eşleşmiyor');
                try {
                  setSaving(true);
                  await api.put('/api/auth/change-password', { oldPassword, newPassword, confirmPassword });
                  alert('Şifre güncellendi');
                  setPwdOpen(false);
                  setOldPassword(''); setNewPassword(''); setConfirmPassword('');
                } catch (e) {
                  alert(e.response?.data?.message || 'Şifre güncellenemedi');
                } finally { setSaving(false); }
              }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </header>
  );
};

export default Header;
